import { useEffect, useMemo, useState } from "react";
import {
  Bell,
  BellRing,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Pencil,
  Smartphone,
  Trash2,
  X,
} from "lucide-react";
import { readableErrorMessage } from "./errorMessage";
import { supabase } from "./supabase";

type PreferenceKey =
  | "signup_received"
  | "payment_received"
  | "manual_review"
  | "technical_failure";

type PushPreferences = Record<PreferenceKey, boolean>;

type SavedSubscription = {
  id: string;
  endpoint: string;
  device_name: string;
  preferences: PushPreferences;
  is_active: boolean;
  last_success_at?: string | null;
  last_error?: string | null;
  created_at: string;
  updated_at: string;
};

type PushConfig = {
  ok: boolean;
  configured: boolean;
  publicKey: string;
  subscriptions: SavedSubscription[];
};

type PreferenceScope = "september_launch" | "general";

type NotificationGuideKey = "iphone" | "ipad" | "android" | "mac" | "windows";

type NotificationGuide = {
  key: NotificationGuideKey;
  label: string;
  description: string;
  steps: string[];
};

const defaultPreferences: PushPreferences = {
  signup_received: true,
  payment_received: true,
  manual_review: true,
  technical_failure: true,
};

const preferenceRows: Array<{
  key: PreferenceKey;
  scope: PreferenceScope;
  title: string;
  description: string;
}> = [
  {
    key: "signup_received",
    scope: "september_launch",
    title: "Nieuwe aanmelding",
    description: "Wanneer iemand het aanmeldformulier voor september 2026 verstuurt.",
  },
  {
    key: "payment_received",
    scope: "september_launch",
    title: "Betaling ontvangen",
    description: "Zodra de eerste factuur uit een aanmelding voor september 2026 als betaald is bevestigd.",
  },
  {
    key: "manual_review",
    scope: "september_launch",
    title: "Handmatige controle nodig",
    description: "Als een aanmelding voor september 2026 controle nodig heeft, bijvoorbeeld bij een dubbeling of btw-/VIES-controle.",
  },
  {
    key: "technical_failure",
    scope: "general",
    title: "Algemene technische fout",
    description: "Alleen als een automatische beheeractie na meerdere pogingen definitief mislukt.",
  },
];

const notificationGuides: NotificationGuide[] = [
  {
    key: "iphone",
    label: "iPhone",
    description: "Gebruik Safari en open de beheertool daarna via het Brede Horizon-icoon.",
    steps: [
      "Open beheer.bredehorizon.com in Safari en log in.",
      "Tik op Deel en kies Zet op beginscherm. Laat Open als webapp aanstaan.",
      "Open de beheertool voortaan via het Brede Horizon-icoon op je beginscherm.",
      "Ga naar Meldingen, tik op Meldingen op dit apparaat aanzetten en kies Sta toe.",
      "Controleer bij Instellingen → Meldingen → Brede Horizon of Meldingen en Badges aanstaan.",
      "Verstuur een testmelding en ga direct terug naar het beginscherm om ook het badgecijfer te controleren.",
    ],
  },
  {
    key: "ipad",
    label: "iPad",
    description: "Gebruik Safari en voeg de beheertool toe aan het beginscherm.",
    steps: [
      "Open beheer.bredehorizon.com in Safari en log in.",
      "Tik op Deel in de Safari-balk en kies Zet op beginscherm. Laat Open als webapp aanstaan.",
      "Open daarna het Brede Horizon-icoon op je beginscherm.",
      "Ga naar Meldingen, tik op Meldingen op dit apparaat aanzetten en kies Sta toe.",
      "Controleer bij Instellingen → Meldingen → Brede Horizon of Meldingen en Badges aanstaan.",
      "Gebruik Testmelding versturen om de installatie te controleren.",
    ],
  },
  {
    key: "android",
    label: "Android",
    description: "Chrome is de aanbevolen browser voor installatie en meldingen.",
    steps: [
      "Open beheer.bredehorizon.com in Chrome en log in.",
      "Tik op het menu met de drie puntjes en kies App installeren of Toevoegen aan startscherm.",
      "Open de beheertool via het nieuwe Brede Horizon-icoon.",
      "Ga naar Meldingen, tik op Meldingen op dit apparaat aanzetten en kies Toestaan.",
      "Controleer in de Android-instellingen bij Apps → Brede Horizon of meldingen en app-icoonbadges zijn toegestaan.",
      "Gebruik Testmelding versturen om de installatie te controleren.",
    ],
  },
  {
    key: "mac",
    label: "Mac",
    description: "Installeer de beheertool als webapp voor een eigen app-icoon en badge.",
    steps: [
      "Open beheer.bredehorizon.com in Safari of Chrome en log in.",
      "Kies in Safari Deel → Zet in Dock. Gebruik in Chrome het installatie-icoon rechts in de adresbalk.",
      "Open de geïnstalleerde Brede Horizon-app vanuit het Dock of via Spotlight.",
      "Ga naar Meldingen, tik op Meldingen op dit apparaat aanzetten en sta meldingen toe.",
      "Controleer bij Systeeminstellingen → Meldingen of Brede Horizon meldingen en badges mag tonen.",
      "Gebruik Testmelding versturen om de installatie te controleren.",
    ],
  },
  {
    key: "windows",
    label: "Windows",
    description: "Gebruik Edge of Chrome en installeer de beheertool als app.",
    steps: [
      "Open beheer.bredehorizon.com in Edge of Chrome en log in.",
      "Klik op het installatie-icoon in de adresbalk. In Edge kan dit ook via … → Apps → Deze site installeren als app.",
      "Open Brede Horizon vervolgens via Start, het bureaublad of de taakbalk.",
      "Ga naar Meldingen, klik op Meldingen op dit apparaat aanzetten en kies Toestaan.",
      "Controleer bij Instellingen → Systeem → Meldingen of Brede Horizon of je browser meldingen mag tonen.",
      "Gebruik Testmelding versturen om de installatie te controleren.",
    ],
  },
];

export function NotificationSettingsPage() {
  const [config, setConfig] = useState<PushConfig | null>(null);
  const [browserSubscription, setBrowserSubscription] = useState<PushSubscription | null>(null);
  const [preferences, setPreferences] = useState<PushPreferences>(defaultPreferences);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editingSubscriptionId, setEditingSubscriptionId] = useState<string | null>(null);
  const [deviceNameDraft, setDeviceNameDraft] = useState("");
  const detectedGuide = detectedNotificationGuide();
  const [openGuide, setOpenGuide] = useState<NotificationGuideKey | null>(() => detectedNotificationGuide());
  const supported = typeof window !== "undefined" &&
    "serviceWorker" in navigator && "PushManager" in window &&
    "Notification" in window;
  const ios = isIos();
  const standalone = isStandalone();

  const currentDevice = useMemo(() => {
    if (!browserSubscription || !config) return null;
    return config.subscriptions.find((item) =>
      item.is_active && item.endpoint === browserSubscription.endpoint
    ) || null;
  }, [browserSubscription, config]);

  useEffect(() => {
    void loadConfig();
  }, []);

  useEffect(() => {
    if (currentDevice?.preferences) setPreferences(currentDevice.preferences);
  }, [currentDevice?.id]);

  async function loadConfig() {
    setError(null);
    try {
      const response = await invokePushFunction({ action: "config" });
      setConfig(response as PushConfig);
      if (supported) {
        const registration = await navigator.serviceWorker.register("/service-worker.js");
        const existing = await registration.pushManager.getSubscription();
        setBrowserSubscription(existing);
      }
    } catch (loadError) {
      setError(readableErrorMessage(loadError, "Notificatie-instellingen laden mislukt."));
    }
  }

  async function enableNotifications() {
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      if (!supported) throw new Error("Deze browser ondersteunt geen webmeldingen.");
      if (ios && !standalone) {
        throw new Error("Voeg de beheertool eerst toe aan je beginscherm en open hem via het nieuwe icoon.");
      }
      if (!config?.configured || !config.publicKey) {
        throw new Error("Webmeldingen zijn nog niet volledig geconfigureerd.");
      }
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        throw new Error("Je telefoon heeft geen toestemming gegeven voor meldingen.");
      }
      const registration = await navigator.serviceWorker.register("/service-worker.js");
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(config.publicKey),
        });
      }
      const json = subscription.toJSON();
      if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
        throw new Error("De browser gaf geen volledig pushabonnement terug.");
      }
      await invokePushFunction({
        action: "subscribe",
        subscription: json,
        deviceName: deviceName(),
        preferences,
      });
      setBrowserSubscription(subscription);
      setMessage("Meldingen zijn op deze telefoon aangezet.");
      await loadConfig();
    } catch (enableError) {
      setError(readableErrorMessage(enableError, "Meldingen aanzetten mislukt."));
    } finally {
      setBusy(false);
    }
  }

  async function updatePreference(key: PreferenceKey, enabled: boolean) {
    const next = { ...preferences, [key]: enabled };
    setPreferences(next);
    if (!currentDevice) return;
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      await invokePushFunction({
        action: "update_preferences",
        subscriptionId: currentDevice.id,
        preferences: next,
      });
      setMessage("Je meldingsvoorkeuren zijn opgeslagen.");
      await loadConfig();
    } catch (preferenceError) {
      setPreferences(preferences);
      setError(readableErrorMessage(preferenceError, "Voorkeur opslaan mislukt."));
    } finally {
      setBusy(false);
    }
  }

  async function sendTest() {
    if (!currentDevice) return;
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      await invokePushFunction({ action: "test", subscriptionId: currentDevice.id });
      setMessage("Testmelding verstuurd. Hij hoort binnen enkele seconden te verschijnen.");
    } catch (testError) {
      setError(readableErrorMessage(testError, "Testmelding versturen mislukt."));
    } finally {
      setBusy(false);
    }
  }

  async function removeDevice(subscription: SavedSubscription) {
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      if (browserSubscription?.endpoint === subscription.endpoint) {
        await browserSubscription.unsubscribe();
        setBrowserSubscription(null);
      }
      await invokePushFunction({ action: "remove", subscriptionId: subscription.id });
      setMessage(`${subscription.device_name} ontvangt geen meldingen meer.`);
      await loadConfig();
    } catch (removeError) {
      setError(readableErrorMessage(removeError, "Apparaat verwijderen mislukt."));
    } finally {
      setBusy(false);
    }
  }

  function startRenamingDevice(subscription: SavedSubscription) {
    setEditingSubscriptionId(subscription.id);
    setDeviceNameDraft(subscription.device_name);
    setMessage(null);
    setError(null);
  }

  function stopRenamingDevice() {
    setEditingSubscriptionId(null);
    setDeviceNameDraft("");
  }

  async function renameDevice(subscription: SavedSubscription) {
    const nextName = deviceNameDraft.trim();
    if (!nextName) {
      setError("Geef het apparaat een naam.");
      return;
    }
    if (nextName === subscription.device_name) {
      stopRenamingDevice();
      return;
    }
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      await invokePushFunction({
        action: "rename",
        subscriptionId: subscription.id,
        deviceName: nextName,
      });
      stopRenamingDevice();
      setMessage(`Apparaatnaam gewijzigd in ${nextName}.`);
      await loadConfig();
    } catch (renameError) {
      setError(readableErrorMessage(renameError, "Apparaatnaam wijzigen mislukt."));
    } finally {
      setBusy(false);
    }
  }

  const permission = supported ? Notification.permission : "unsupported";
  const mustInstallOnIos = ios && !standalone;
  const canEnable = supported && !mustInstallOnIos && Boolean(config?.configured);

  function renderPreferenceRows(scope: PreferenceScope) {
    return (
      <div className="notification-preference-list">
        {preferenceRows.filter((row) => row.scope === scope).map((row) => (
          <label className="notification-preference-row" key={row.key}>
            <span>
              <strong>{row.title}</strong>
              <small>{row.description}</small>
            </span>
            <input
              type="checkbox"
              checked={preferences[row.key]}
              disabled={busy}
              onChange={(event) => void updatePreference(row.key, event.target.checked)}
            />
          </label>
        ))}
      </div>
    );
  }

  return (
    <section className="page notification-settings-page">
      <div className="page-header">
        <div>
          <h1>Meldingen</h1>
          <p className="page-description">Ontvang belangrijke Brede Horizon-gebeurtenissen rechtstreeks op je eigen telefoon of computer.</p>
        </div>
      </div>

      {message && <div className="banner success">{message}</div>}
      {error && <div className="banner error">{error}</div>}

      <div className="notification-settings-grid">
        <section className="panel notification-device-panel">
          <div className="notification-panel-heading">
            <span className={`notification-device-icon ${currentDevice ? "active" : ""}`}>
              {currentDevice ? <BellRing size={22} /> : <Smartphone size={22} />}
            </span>
            <div>
              <div className="panel-title">Dit apparaat</div>
              <p className="muted">{currentDevice?.device_name || deviceName()}</p>
            </div>
          </div>

          {currentDevice ? (
            <>
              <div className="notification-status success">
                <CheckCircle2 size={17} /> Meldingen staan aan
              </div>
              <div className="row-actions notification-actions">
                <button className="primary-button" type="button" disabled={busy} onClick={sendTest}>
                  <Bell size={16} /> Testmelding versturen
                </button>
                <button className="secondary-button" type="button" disabled={busy} onClick={() => removeDevice(currentDevice)}>
                  <Trash2 size={15} /> Uitzetten op dit apparaat
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="notification-status muted-status">
                <Bell size={17} /> Meldingen staan nog niet aan
              </div>
              {mustInstallOnIos && (
                <div className="notification-instructions">
                  <strong>Eerst toevoegen aan je {detectedGuide === "ipad" ? "iPad" : "iPhone"}-beginscherm</strong>
                  <p>Volg hieronder de geopende {detectedGuide === "ipad" ? "iPad" : "iPhone"}-uitleg. Daarna kun je de knop voor meldingen gebruiken.</p>
                </div>
              )}
              {!supported && !mustInstallOnIos && <div className="banner warning compact-banner">Deze browser ondersteunt geen webmeldingen. Probeer Safari, Chrome, Edge of Firefox op een recent apparaat.</div>}
              {supported && config && !config.configured && <div className="banner warning compact-banner">De beveiligde pushsleutels ontbreken nog op de server.</div>}
              <button className="primary-button" type="button" disabled={busy || !canEnable} onClick={enableNotifications}>
                <BellRing size={16} /> {busy ? "Bezig…" : "Meldingen op dit apparaat aanzetten"}
              </button>
              <p className="notification-permission-note">Browsertoestemming: <strong>{permissionLabel(permission)}</strong>. De beheertool vraagt pas toestemming nadat je zelf op de knop tikt.</p>
            </>
          )}
        </section>

        <div className="notification-preferences-column">
          <section className="panel notification-preferences-panel notification-scope-panel launch-scope">
            <div className="panel-title">Lancering september 2026</div>
            <p className="muted">
              Deze meldingen horen alleen bij het speciale aanmeldformulier en de eerste factuur van deze lancering.
              Gewone vervolgfacturen en periodieke betalingen van bestaande klanten vallen hier niet onder.
            </p>
            {renderPreferenceRows("september_launch")}
          </section>

          <section className="panel notification-preferences-panel notification-scope-panel general-scope">
            <div className="panel-title">Algemene beheertool</div>
            <p className="muted">
              Deze melding staat los van de septemberlancering en geldt voor automatische beheerprocessen.
            </p>
            {renderPreferenceRows("general")}
          </section>
          <p className="notification-device-choice-note">Deze keuzes gelden alleen voor dit apparaat.</p>
        </div>
      </div>

      <section className="panel notification-help-panel">
        <div className="panel-title">Hulp per apparaat</div>
        <p className="muted">Kies je apparaat en volg de stappen. De uitleg voor het herkende apparaat staat automatisch open.</p>
        <div className="notification-guide-list">
          {notificationGuides.map((guide) => {
            const expanded = openGuide === guide.key;
            const isDetected = detectedGuide === guide.key;
            return (
              <div className={`notification-guide ${expanded ? "expanded" : ""}`} key={guide.key}>
                <button
                  className="notification-guide-toggle"
                  type="button"
                  aria-expanded={expanded}
                  onClick={() => setOpenGuide(expanded ? null : guide.key)}
                >
                  <span>
                    <strong>{guide.label}</strong>
                    {isDetected && <small>Dit apparaat</small>}
                  </span>
                  {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>
                {expanded && (
                  <div className="notification-guide-content">
                    <p>{guide.description}</p>
                    <ol>
                      {guide.steps.map((step) => <li key={step}>{step}</li>)}
                    </ol>
                    <p className="notification-guide-tip"><strong>Al geïnstalleerd?</strong> Begin dan bij de stap waarin je de Brede Horizon-app via het icoon opent.</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="panel notification-devices-list">
        <div className="panel-title">Gekoppelde apparaten</div>
        <p className="muted notification-devices-explanation">Je ziet hier alleen apparaten die met jouw eigen beheerdersaccount zijn gekoppeld.</p>
        {!config && <div className="muted">Apparaten laden…</div>}
        {config && config.subscriptions.length === 0 && <div className="muted">Nog geen apparaten gekoppeld.</div>}
        {config?.subscriptions.map((subscription) => (
          <div className="notification-device-row" key={subscription.id}>
            <span className={`notification-device-dot ${subscription.is_active ? "active" : ""}`} />
            {editingSubscriptionId === subscription.id ? (
              <form className="notification-device-name-form" onSubmit={(event) => {
                event.preventDefault();
                void renameDevice(subscription);
              }}>
                <input
                  type="text"
                  value={deviceNameDraft}
                  maxLength={120}
                  autoFocus
                  disabled={busy}
                  onChange={(event) => setDeviceNameDraft(event.target.value)}
                  aria-label="Nieuwe apparaatnaam"
                />
                <button className="icon-button notification-device-save" type="submit" disabled={busy || !deviceNameDraft.trim()} title="Naam opslaan" aria-label="Naam opslaan">
                  <Check size={15} />
                </button>
                <button className="icon-button" type="button" disabled={busy} onClick={stopRenamingDevice} title="Annuleren" aria-label="Naam wijzigen annuleren">
                  <X size={15} />
                </button>
              </form>
            ) : (
              <>
                <span>
                  <strong>{subscription.device_name}</strong>
                  <small>{subscription.endpoint === browserSubscription?.endpoint ? "Dit apparaat" : subscription.is_active ? "Actief" : "Niet actief"}</small>
                </span>
                <span className="notification-device-row-actions">
                  <button className="icon-button" type="button" disabled={busy} onClick={() => startRenamingDevice(subscription)} title="Apparaatnaam wijzigen" aria-label={`${subscription.device_name} hernoemen`}>
                    <Pencil size={15} />
                  </button>
                  <button className="icon-button danger" type="button" disabled={busy} onClick={() => void removeDevice(subscription)} title="Apparaat verwijderen" aria-label={`${subscription.device_name} verwijderen`}>
                    <Trash2 size={15} />
                  </button>
                </span>
              </>
            )}
          </div>
        ))}
      </section>

      <p className="notification-privacy-note">Op het vergrendelscherm tonen we geen adres, volledig btw-nummer of technische foutdetails. E-mail blijft als vangnet actief.</p>
    </section>
  );
}

async function invokePushFunction(body: Record<string, unknown>) {
  if (!supabase) throw new Error("Supabase is niet geconfigureerd.");

  const sessionResult = await supabase.auth.getSession();
  if (sessionResult.error) throw sessionResult.error;
  const accessToken = sessionResult.data.session?.access_token;
  if (!accessToken) throw new Error("Je sessie op dit apparaat is verlopen. Log opnieuw in.");

  let { data, error } = await supabase.functions.invoke("push-notifications", {
    body,
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (error && await pushFunctionErrorDetail(error) === "Unauthorized") {
    const refreshed = await supabase.auth.refreshSession();
    const refreshedToken = refreshed.data.session?.access_token;
    if (refreshed.error || !refreshedToken) {
      await supabase.auth.signOut({ scope: "local" });
      throw new Error("Je sessie op dit apparaat was verlopen. Log hier opnieuw in; je andere apparaten blijven ingelogd.");
    }
    ({ data, error } = await supabase.functions.invoke("push-notifications", {
      body,
      headers: { Authorization: `Bearer ${refreshedToken}` },
    }));
  }

  if (error) {
    const detail = await pushFunctionErrorDetail(error);
    throw new Error(detail);
  }
  if (data?.error) throw new Error(String(data.error));
  return data;
}

async function pushFunctionErrorDetail(error: { message: string; context?: Response }) {
  let detail = error.message;
  if (error.context) {
    try {
      const payload = await error.context.clone().json() as { error?: string };
      if (payload.error) detail = payload.error;
    } catch {
      // Houd de foutmelding van de client aan.
    }
  }
  return detail;
}

function urlBase64ToUint8Array(value: string) {
  const padding = "=".repeat((4 - value.length % 4) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let index = 0; index < raw.length; index += 1) {
    output[index] = raw.charCodeAt(index);
  }
  return output;
}

function isIos() {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(display-mode: standalone)").matches ||
    Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
}

function detectedNotificationGuide(): NotificationGuideKey | null {
  if (typeof navigator === "undefined") return null;
  const agent = navigator.userAgent;
  if (/iphone|ipod/i.test(agent)) return "iphone";
  if (/ipad/i.test(agent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) return "ipad";
  if (/android/i.test(agent)) return "android";
  if (/windows/i.test(agent)) return "windows";
  if (/macintosh|mac os x/i.test(agent)) return "mac";
  return null;
}

function deviceName() {
  if (typeof navigator === "undefined") return "Onbekend apparaat";
  const agent = navigator.userAgent;
  const device = /iphone/i.test(agent)
    ? "iPhone"
    : /ipad/i.test(agent)
    ? "iPad"
    : /android/i.test(agent)
    ? "Android"
    : /macintosh|mac os x/i.test(agent)
    ? "Mac"
    : /windows/i.test(agent)
    ? "Windows-computer"
    : "Apparaat";
  const browser = /edg/i.test(agent)
    ? "Edge"
    : /firefox|fxios/i.test(agent)
    ? "Firefox"
    : /crios|chrome/i.test(agent)
    ? "Chrome"
    : /safari/i.test(agent)
    ? "Safari"
    : "browser";
  return `${device} · ${browser}`;
}

function permissionLabel(value: NotificationPermission | "unsupported") {
  if (value === "granted") return "toegestaan";
  if (value === "denied") return "geblokkeerd";
  if (value === "default") return "nog niet gevraagd";
  return "niet ondersteund";
}
